import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import { useMemo } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import DataTable from '@helios/DataTable';
import PageContent from '@helios/PageContent';
import PageIntro from '@helios/PageIntro';
import Layout from '@/components/Layout.js';
import { allSites } from '@/utils/domains.js';
import useCampus from '@/hooks/useCampus.ts';
import Spinner from '@helios/Spinner';
import styles from '@/styles/DisclosuresPage.module.scss';
export const getStaticPaths = async () => {
  return {
    paths: allSites.map(site => ({
      params: {
        site
      }
    })),
    fallback: false
  };
};
export async function getStaticProps({
  params
}) {
  return {
    props: {
      site: params.site
    }
  };
}

// A CMS table without a heading before it is named after the page.
const FALLBACK_TABLE_LABEL = 'Disclosures';
function textOf(node) {
  if (node.nodeType === 'text') return node.value;
  return (node.content ?? []).map(textOf).join('');
}

/**
 * Each CMS table's accessible name: the text of the heading the editors put
 * above it ("Catalog & Regulatory", "Title IX and Safety and Security", …),
 * so every table region is named by its own section. Keyed by node: the
 * renderer hands the same node objects to `renderNode`.
 */
function tableLabels(document) {
  const labels = new WeakMap();
  let heading = null;
  for (const node of document?.content ?? []) {
    if (node.nodeType.startsWith('heading-')) {
      heading = textOf(node).trim() || null;
    } else if (node.nodeType === 'table') {
      labels.set(node, heading ?? FALLBACK_TABLE_LABEL);
    }
  }
  return labels;
}

// Link text that is itself a URL or an e-mail address: one run with no
// space, which the browser does not break, so it set its column's width (the
// Agency Bodies table scrolled 91px at 393; O289). The link is marked
// `data-url-text` and the module lets it break anywhere; link text in words
// keeps breaking between words.
const URL_TEXT = /^(?:https?:\/\/|www\.)\S+$|^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function richTextOptions(labels) {
  return {
    renderNode: {
      table: (node, children) => <div className={styles['table-block']}>
          <DataTable label={labels.get(node) ?? FALLBACK_TABLE_LABEL}>
            <tbody>{children}</tbody>
          </DataTable>
        </div>,
      'table-row': (node, children) => <tr>{children}</tr>,
      'table-cell': (node, children) => <td className={styles.cell}>{children}</td>,
      hyperlink: (node, children) => <a href={node.data.uri} target="_blank" rel="noreferrer" data-url-text={URL_TEXT.test(textOf(node).trim()) ? '' : undefined}>
          {children}
        </a>
    }
  };
}
function Disclosures(t0) {
  const $ = _c(16);
  const {
    site
  } = t0;
  const {
    disclosuresCMSQuery
  } = useCampus();
  const data = disclosuresCMSQuery?.data;
  const isLoading = disclosuresCMSQuery.isLoading;
  data?.content;
  let t1;
  if ($[0] !== data?.content) {
    t1 = richTextOptions(tableLabels(data?.content));
    $[0] = data?.content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Head><title>Disclosures | Campus</title><link rel="canonical" href="https://campus.edu/disclosures" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="Access important information and documents pertaining to Campus." /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = data?.title || "Disclosures";
  const t4 = data?.description;
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = <PageIntro align="center" title={t3} subtitle={t4} subtitleVariant="body" />;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== data || $[7] !== isLoading || $[8] !== options) {
    t6 = isLoading ? <Spinner /> : <div className={styles.content}>{data?.content ? documentToReactComponents(data.content, options) : <p className={styles.empty}>No disclosure content available.</p>}</div>;
    $[6] = data;
    $[7] = isLoading;
    $[8] = options;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div id="wrapper">{t2}<PageContent>{t5}{t6}</PageContent></div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== site || $[14] !== t7) {
    t8 = <Layout site={site}>{t7}</Layout>;
    $[13] = site;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
export default Disclosures;